import classNames from 'classnames';
import _round from 'lodash/round';
import React, { useCallback, useState, useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

import { getProductTypeDisplayName, getProductTypeIcon } from './OrderSummaryGrid';
import { ReactComponent as WarningTriangle } from '../../../../media/images/warning-triangle.svg';

import { removeItemFromCart } from 'common/api/e-comm/cart/lineitem';
import { CartLineItem, CartLineItemGiftDetails } from 'common/api/e-comm/models/CartLineItem';
import { requestCartItems, resetCart } from 'common/features/store/duck/cart/duck';
import { monetizeWithDecimal } from 'common/utils/strings';
import { createStyles, createStyle } from 'utils/createStyle';
import { getProductName, getWalletCardDisplayName } from 'features/order-summary/utils';
import { ProductType } from 'common/api/e-comm/models/Product';
import { useAlert } from 'modules/alerts';
import { formatDate } from 'common/utils/time';
import { LinkBtn } from 'components/link-btn';
import { useCardsFromWallet } from 'hooks/useCardsFromWallet';
import { GiftCard, EGiftCard } from 'common/api/e-comm/models/Card';
import { FeatureFlag } from 'components/feature-flag';
import { QuantityRenderer } from './QuantityRenderer';
import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import useMediaQueries from 'hooks/useMediaQueries';
import { Modal, ModalSize } from 'components/modal/Modal';
import { isInIframe, mainWebsiteOrigin } from 'utils';

const styles = createStyles({
    container: 'lg:p-2',
    header: createStyle('pt-2'),
    headerShipToHome: createStyle('duration-300 border-solid border-b border-grey-3', {
        background:
            'linear-gradient(180deg, rgba(241, 90, 41, 0.1) 0%, rgba(230, 18, 106, 0.1) 42.35%, rgba(126, 63, 151, 0.1) 88.48%)',
    }),
    bigBtn: createStyle('border border-brand-accent bg-white px-8 py-2 w-full', {
        borderRadius: '10px',
        boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1)',
    }),
    bigBtnUnselected: createStyle('border border-grey-2 bg-white/75 px-8 py-2 text-grey-2 w-full', {
        borderRadius: '10px',
    }),
    shippingOptionButtons: createStyle('block lg:grid gap-2 py-4 px-4', {
        gridTemplateColumns: '1fr 1fr',
    }),
    summaryGroupItems: createStyle('grid w-full', {
        gridTemplateColumns: '2fr 1fr 1fr 1fr 2rem',
        rowGap: '1rem',
    }),
    productTypeSeparator: createStyle('py-4 px-8'),
    columns: createStyle('', {
        backgroundColor: '#F7F7F7',
        borderRadius: '10px 10px 0 0',
    }),
    footer: createStyle('', {
        backgroundColor: '#F7F7F7',
        borderRadius: '0 0 10px 10px',
    }),
});

export const RenderItems = ({
    items,
    removeCartItem,
    loading,
    hideButtons = false,
    showEgiftDetails,
    walletCards,
    setShowItemUpdateErrorModal,
    hideDeleteButton = false,
}: {
    items: CartLineItem[];
    removeCartItem: (id: string) => void;
    loading: boolean;
    hideButtons: boolean;
    showEgiftDetails: (giftCardDetail: CartLineItemGiftDetails | undefined) => void;
    walletCards: (GiftCard | EGiftCard)[];
    setShowItemUpdateErrorModal: (_: boolean) => void;
    hideDeleteButton: boolean;
}) => {
    const { updatingLineItem } = useContext(OrderScreenContext);

    return (
        <>
            {items.map((item) => (
                <React.Fragment key={item.id}>
                    {loading && (
                        <>
                            <div className="col-span-6">
                                <Skeleton style={{ height: '4rem' }} />
                            </div>
                        </>
                    )}
                    {!loading && (
                        <>
                            <div className="text-sm truncate">
                                <div className="flex text-lg text-brand-dark align-middle truncate relative">
                                    <span className="inline-block align-text-bottom font-bold truncate w-full">
                                        {getProductName(item)}
                                    </span>
                                </div>
                                <div className="text-sm text-grey-1 truncate mt-1">
                                    <span className="flex items-center float-left mr-1">
                                        {getProductTypeIcon(item.product.type)}
                                    </span>
                                    <span>{getProductTypeDisplayName(item.product.type)}</span>
                                    {item.product.type === ProductType.EGiftCard &&
                                    item.giftDetail ? (
                                        <>
                                            <span> - </span>
                                            <LinkBtn
                                                className="text-brand italic underline"
                                                onClick={() => showEgiftDetails(item.giftDetail)}
                                            >
                                                Gift Details{' '}
                                            </LinkBtn>
                                        </>
                                    ) : null}
                                    <FeatureFlag flagKey="enable_custom_card_names" fallback={true}>
                                        {getWalletCardDisplayName(item, walletCards)
                                            ? ` - ${getWalletCardDisplayName(item, walletCards)}`
                                            : ''}
                                    </FeatureFlag>
                                </div>
                                <div className="text-grey-1 mt-1">
                                    Earn{' '}
                                    {_round((item.earnings / item.subtotal) * 100, 2).toFixed(2)}% ={' '}
                                    <span className="text-success">
                                        {monetizeWithDecimal(item.earnings)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center justify-center text-center text-sm text-grey-1">
                                {monetizeWithDecimal(item.price)}
                            </div>
                            <QuantityRenderer
                                setShowItemUpdateErrorModal={setShowItemUpdateErrorModal}
                                hideButtons={hideButtons}
                                item={item}
                            />
                            <div className="flex items-center justify-center text-center text-sm font-bold text-brand-dark">
                                {monetizeWithDecimal(item.subtotal)}
                            </div>
                            <div className="flex items-center justify-center text-center text-sm font-bold">
                                {!hideDeleteButton && (
                                    <button
                                        disabled={updatingLineItem}
                                        onClick={() => removeCartItem(item.id)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            className="text-xl text-grey-2"
                                        />
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </React.Fragment>
            ))}
        </>
    );
};

// TODO: Refactor both RenderItems & RenderItemsMobile
export const RenderItemsMobile = ({
    items,
    removeCartItem,
    loading,
    hideButtons = false,
    showEgiftDetails,
    walletCards,
    setShowItemUpdateErrorModal,
    hideDeleteButton = false,
}: {
    items: CartLineItem[];
    removeCartItem: (id: string) => void;
    loading: boolean;
    hideButtons: boolean;
    showEgiftDetails: (giftCardDetail: CartLineItemGiftDetails | undefined) => void;
    walletCards: (GiftCard | EGiftCard)[];
    setShowItemUpdateErrorModal: (_: boolean) => void;
    hideDeleteButton?: boolean;
}) => {
    return (
        <>
            {items.map((item) => {
                return (
                    <React.Fragment key={item.id}>
                        {loading && (
                            <div key={item.id}>
                                <Skeleton height="4rem" style={{ marginBottom: '1rem' }} />
                            </div>
                        )}
                        {!loading && (
                            <div key={item.id} className="flex flex-col gap-3 mb-10">
                                <div className="text-sm truncate">
                                    <div className="flex justify-between gap-6">
                                        <div className="font-semibold flex flex-col text-lg text-brand-dark align-middle truncate relative">
                                            <span className="whitespace-normal break-words">
                                                {getProductName(item)}
                                            </span>
                                            <div className="mt-1 text-sm text-grey-1 truncate">
                                                <span className="mr-2">
                                                    {getProductTypeIcon(item.product.type, '1rem')}
                                                </span>
                                                <span className="font-normal">
                                                    {getProductTypeDisplayName(item.product.type)}
                                                </span>
                                                {item.product.type === ProductType.EGiftCard &&
                                                item.giftDetail ? (
                                                    <>
                                                        <span> - </span>
                                                        <LinkBtn
                                                            className="text-brand italic underline"
                                                            onClick={() =>
                                                                showEgiftDetails(item.giftDetail)
                                                            }
                                                        >
                                                            Gift Details{' '}
                                                        </LinkBtn>
                                                    </>
                                                ) : null}
                                                <FeatureFlag
                                                    flagKey="enable_custom_card_names"
                                                    fallback={true}
                                                >
                                                    <span className="whitespace-normal break-words">
                                                        {getWalletCardDisplayName(item, walletCards)
                                                            ? ` - ${getWalletCardDisplayName(
                                                                  item,
                                                                  walletCards
                                                              )}`
                                                            : ''}
                                                    </span>
                                                </FeatureFlag>
                                            </div>
                                        </div>
                                        <div>
                                            {!hideButtons && (
                                                <QuantityRenderer
                                                    setShowItemUpdateErrorModal={
                                                        setShowItemUpdateErrorModal
                                                    }
                                                    hideButtons={hideButtons}
                                                    item={item}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-between mt-1">
                                        <div className="mr-auto text-grey-1 inline-block ">
                                            Earn{' '}
                                            {_round(
                                                (item.earnings / item.subtotal) * 100,
                                                2
                                            ).toFixed(2)}
                                            % ={' '}
                                            <span className="text-success">
                                                {monetizeWithDecimal(item.earnings)}
                                            </span>
                                        </div>
                                        <div className="text-center text-sm text-grey-1">
                                            <span>
                                                {`${item.quantity} x `}{' '}
                                                {monetizeWithDecimal(item.price)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between items-end">
                                    <div>
                                        {!hideDeleteButton && (
                                            <button
                                                onClick={() => removeCartItem(item.id)}
                                                className="text-brand text-sm underline"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <div className="ml-auto text-right inline-block text-sm font-bold text-brand-dark">
                                        Amount {monetizeWithDecimal(item.subtotal)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
};

export interface OrderSummaryGroupItemsProps {
    items: CartLineItem[];
    loading: boolean;
    hideButtons?: boolean;
    hideDeleteButton?: boolean;
}

export const OrderSummaryGroupItems = ({
    items,
    loading,
    hideButtons,
    hideDeleteButton = false,
}: OrderSummaryGroupItemsProps) => {
    const dispatch = useDispatch<any>();
    const [isRemoving, setIsRemoving] = useState(false);
    const alert = useAlert();
    const walletCards = useCardsFromWallet();
    const isDesktopView = useDetectDesktopView();
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

    const showEgiftDetails = (giftDetail: CartLineItemGiftDetails | undefined) => {
        alert({
            title: <span className="sm:pr-16 pr-16">eGift Details</span>,
            message: (
                <div className="w-full text-grey-2 mt-2 sm:text-lg text-sm">
                    <p>{`Scheduled Delivery: ${
                        giftDetail?.deliveryDate ? formatDate(giftDetail.deliveryDate) : 'N/A'
                    }`}</p>
                    <p>{`To: ${giftDetail?.toName || ''}`}</p>
                    <p>{`Email: ${giftDetail?.toEmail || ''}`}</p>
                    <p>{`Message: ${giftDetail?.message || ''}`}</p>
                </div>
            ),
            showCloseButton: true,
            buttons: [{ text: 'Close', secondary: true, className: 'font-bold' }],
        });
    };

    const removeCartItem = useCallback(
        async (id: string) => {
            setIsRemoving(true);
            const response = await removeItemFromCart(id);
            if (response.response?.ok && !response.data) {
                dispatch(resetCart());
                if (isInIframe()) {
                    window.parent.postMessage({ type: 'resetCart' }, mainWebsiteOrigin());
                }
            }
            await requestCartItems()(dispatch);
            setIsRemoving(false);
            if (isInIframe()) {
                window.parent.postMessage({ type: 'cartUpdated' }, mainWebsiteOrigin());
            }
        },
        [dispatch]
    );

    return (
        <div>
            <QuantityEditErrorModal isOpen={showErrorModal} setIsOpen={setShowErrorModal} />
            {isDesktopView ? (
                <div className={classNames(styles.summaryGroupItems, 'hidden lg:grid')}>
                    <RenderItems
                        items={items}
                        removeCartItem={removeCartItem}
                        loading={loading || isRemoving}
                        hideButtons={Boolean(hideButtons)}
                        showEgiftDetails={showEgiftDetails}
                        walletCards={walletCards}
                        setShowItemUpdateErrorModal={setShowErrorModal}
                        hideDeleteButton={hideDeleteButton}
                    />
                </div>
            ) : (
                <div className="block lg:hidden rounded">
                    <RenderItemsMobile
                        items={items}
                        removeCartItem={removeCartItem}
                        loading={loading || isRemoving}
                        hideButtons={Boolean(hideButtons)}
                        showEgiftDetails={showEgiftDetails}
                        walletCards={walletCards}
                        setShowItemUpdateErrorModal={setShowErrorModal}
                        hideDeleteButton={hideDeleteButton}
                    />
                </div>
            )}
        </div>
    );
};

const QuantityEditErrorModal = ({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    return (
        <Modal
            titleStyles="text-center"
            isOpen={isOpen}
            title={''}
            onClose={() => setIsOpen(false)}
            size={[ModalSize.MEDIUM, ModalSize.SMALL]}
        >
            <div className="mx-auto mt-3 text-center">
                <div className="flex flex-col items-center mx-auto">
                    <p className="font-light">
                        <WarningTriangle />
                    </p>
                    <p className="mt-6 mb-1 sm:mb-3 inline text-brand-dark text-22px sm:text-29px font-semibold">
                        Unable to update cart
                    </p>
                </div>
                <p className="text-base text-grey-1 sm:text-lg">Please try again</p>
                <button
                    className="my-5 text-lg underline text-brand font-semibold mx-auto block"
                    onClick={() => setIsOpen(false)}
                >
                    Close
                </button>
            </div>
        </Modal>
    );
};

const useDetectDesktopView = () => {
    const [inDesktopView, setInDesktopView] = useState<boolean>(true);

    const query = useMemo(
        () => ({
            '(max-width: 1024px)': () => {
                setInDesktopView(false);
            },
            '(min-width: 1024px)': () => {
                setInDesktopView(true);
            },
        }),
        []
    );

    useMediaQueries(query);
    return inDesktopView;
};
